/*
  shintop-gallery — upgrades the product gallery's CSS scroll track into a browsable stage
  with a fitted enlarged viewer.

  WHY. Naoya, 2026-09-12, with a mockup: "Can it be look like the on mock for the product
  images?" Dawn's lightbox opened a 2048px packshot at natural size inside a scroller, so the
  "enlarged" view arrived cropped, with an × as its only control. This file owns the viewer.

  FOUR THINGS THAT MAKE IT SAFE

  1. IT DEGRADES TO THE MARKUP. Every control it touches already works with the script absent:
     the stage is a scroll-snap track, the tiles are anchors to slide ids, and the stage and
     the "Take a closer look" control are LINKS to the full-size image file. With JS off the
     reader still browses every photograph and still gets the big picture — in a browser tab
     instead of a dialog.
  2. THE ARROWS ARE CREATED HERE, never rendered in Liquid — a dead arrow is worse than none,
     and a `hidden` attribute is the browser's to remove, not a state to style (§4 rule 37).
  3. NO STATE IS INVENTED. The counter, the label and the viewer title are read off the slide
     the reader is actually looking at, through an IntersectionObserver on the track — never
     from a count this file keeps for itself.
  4. ONE PHOTOGRAPH IS NOT A GALLERY. With a single slide the arrows, the counter, the tiles
     and the viewer's browse controls are all absent; the stage and the viewer still work.
*/

class ShintopGallery extends HTMLElement {
  connectedCallback() {
    if (this.initialised) return;
    this.initialised = true;

    this.track = this.querySelector('[data-shintop-track]');
    this.slides = Array.from(this.querySelectorAll('[data-shintop-slide]'));
    this.tiles = Array.from(this.querySelectorAll('[data-shintop-tile]'));
    this.viewer = this.querySelector('[data-shintop-viewer]');
    this.index = 0;

    this.measureStickyColumn();

    if (!this.track || this.slides.length === 0) return;

    this.watchTrack();
    this.wireTiles();
    this.wireSlideFocus();
    this.wireViewer();

    if (this.slides.length > 1) {
      this.buildClones();
      this.addArrows();
      this.wireKeys();
    }
  }

  /* ---------- which photograph is on screen ---------- */

  watchTrack() {
    if (!('IntersectionObserver' in window)) return;

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (this.scripted) return;
          if (!entry.isIntersecting || entry.intersectionRatio < 0.6) return;
          const index = this.slides.indexOf(entry.target);
          if (index >= 0) this.setIndex(index);
        });
      },
      { root: this.track, threshold: [0.6] }
    );

    this.slides.forEach((slide) => this.observer.observe(slide));
  }

  setIndex(index) {
    this.index = index;
    const slide = this.slides[index];
    const label = slide.getAttribute('data-label') || '';

    const counter = this.querySelector('[data-shintop-index]');
    if (counter) counter.textContent = String(index + 1).padStart(2, '0');

    const labelEl = this.querySelector('[data-shintop-label]');
    if (labelEl) labelEl.textContent = label;

    this.slides.forEach((el, i) => el.setAttribute('aria-current', String(i === index)));
    this.tiles.forEach((el, i) => el.setAttribute('aria-current', String(i === index)));

    const expand = this.querySelector('[data-shintop-expand]');
    if (expand) expand.setAttribute('href', slide.getAttribute('href'));

    const status = this.querySelector('[data-shintop-status]');
    if (status) status.textContent = this.statusText(index);

    /*
      KEYBOARD FOCUS FOLLOWS THE INDEX, HERE AND NOWHERE ELSE (audit 2026-10-14, R08-1: S03-006,
      S08-001). Focus used to be moved at ONE call site, the arrow-key handler, right after its
      goTo() -- so every other way the index moves left it behind. A key pressed during a wrap is
      queued and runs a frame after the landing, long after that handler returned: measured on
      UFO at 1440, two ArrowRights from photograph 5 showed photograph 2 with focus on slide 1,
      and the next Enter would have opened the wrong picture. Here every move counts.

      Only when focus is ON A SLIDE -- never "inside the stage", where the arrows and the
      expand link also live and must keep it. Never while `viewerReturning` is raised: that is
      the dialog's own focus hand-back (wireSlideFocus), and its window stays exactly as it is.
      AFTER `this.index` is set, so the slide's focus handler sees nothing to do and does not
      re-enter goTo(). `preventScroll`, because a wrap is mid-animation into a clone.
    */
    const focused = document.activeElement;
    if (!this.viewerReturning && focused !== slide && this.slides.includes(focused)) {
      slide.focus({ preventScroll: true });
    }

    if (this.viewer && this.viewer.open) this.paintViewer();
  }

  /*
    S07-05: the label goes in through a REPLACER FUNCTION, not as a replacement string.

    `String.replace` reads `$&`, `$\``, `$'` and `$n` in the replacement and substitutes
    around the match, so an alt reading `Sauce $& pack` announced `Sauce %label% pack` —
    the placeholder itself, spoken aloud. The audit report prescribed `replaceAll`; that
    is wrong, and it was measured wrong in this browser before this line was written:
    `replaceAll` has the identical replacement-pattern semantics and corrupts the same
    string the same way (evidence/chunk-05/red-alt-roundtrip-1440.json). A function
    replacer is the fix, because its return value is used literally, by specification.

    The index and total are numbers and could never carry a `$`, but they go through the
    same door so that nobody has to work out which of the three was the dangerous one.

    One sentence for both live regions -- the stage's and, since S06-010, the viewer's -- so
    the two can never describe one photograph two ways.
  */
  statusText(index) {
    const template = this.getAttribute('data-status-template');
    if (!template) return '';
    return template
      .replace('%index%', () => String(index + 1))
      .replace('%total%', () => String(this.slides.length))
      .replace('%label%', () => this.slides[index].getAttribute('data-label') || '');
  }

  /*
    THE GALLERY IS A RING, AND PHOTOGRAPH ONE SITS TO THE RIGHT OF THE LAST ONE.

    Naoya, 2026-09-13, twice. First: "after the last image when you click to the right arrow
    again, it would not do anything… it should go back to the 1st image. This applies to the
    opposite direction and the ones in modal as well." Then, on the first attempt: "the 5 of 5
    part would go a bit weird. It does quickly move numbers through other numbers" and "i want it
    to look like 1st image is on the right of the 5th image, not going back like this."

    WHY THE FIRST ATTEMPT WENT BACKWARDS. It asked for `behavior: 'auto'`, believing that meant
    instant. It does not — `auto` means *use the element's computed `scroll-behavior`*, and this
    track declares `smooth`. So the wrap animated the whole way back across four photographs, and
    the IntersectionObserver dutifully reported each one as it passed: 05, 04, 03, 02, 01.

    WHAT IT DOES NOW. A clone of photograph one is parked to the RIGHT of the last slide and a
    clone of the last is parked to the LEFT of photograph one, both `hidden` until a wrap is
    actually running. A wrap therefore scrolls ONE slide in the direction the arrow points, into
    a picture that is already the right picture; when the scroll settles, the track is silently
    repositioned onto the real slide at the identical visual position and the clone goes away.
    Nothing is crossed, so nothing can be counted on the way past.

    The clones are `hidden` at rest rather than merely off to the side, so a swipe can never
    land on one and no product page ever loads a sixth photograph.
  */

  /*
    THE STAGE AND THE VIEWER SHARE AN INDEX, NOT A SCROLL POSITION.

    Naoya, 2026-09-13: "does it have to be linked to the one in main? When clicking right arrow
    quick, sometimes it shows some weird movement. After going through 5 and 1, when i go to 2, it
    would shows some weird movement."

    No, it does not, and the link was the bug. The viewer's arrows used to drive the track hidden
    behind the dialog, so every animation, clone and settle-timer in the stage ran underneath a
    photograph the reader was looking at somewhere else — and whatever state that left behind was
    the state the next stage click started from. `select()` is now the whole of what the viewer
    needs: the index, the picture, the counter. The track is put back in step in one instant,
    invisible move when the dialog closes.
  */
  select(index) {
    const total = this.slides.length;
    this.setIndex(((index % total) + total) % total);
  }

  goTo(index, behaviour) {
    if (this.viewer && this.viewer.open) return this.select(index);

    const total = this.slides.length;
    const wrapped = ((index % total) + total) % total;

    // A SECOND CLICK DOES NOT STACK A SECOND ANIMATION ON AN ABANDONED ONE — that is what he saw
    // as "weird movement": the track snapped back to the slide the first click was still
    // travelling to, and only then set off again. There are two interruptions and they want
    // different answers:
    //
    //   a plain move still running — RETARGET it. The browser redirects a smooth scroll in
    //   flight without any discontinuity, so rapid clicking reads as one accelerating slide.
    //   a WRAP still running — land it and jump. Its clone is about to be hidden out from under
    //   the animation, and no smooth path exists from a clone at one end of the track to a real
    //   slide at the other.
    /*
      A CONTROL USED DURING A WRAP IS QUEUED, NOT JUMPED -- round 29, 2026-09-19. Naoya: "there
      are no transition for this, and it instantly move to the next image."

      Round 24 landed the wrap and jumped straight to the new target, because no smooth path exists
      from a clone at one end of the strip to a real slide at the other. It is true, and the reader
      sees it as the picture teleporting: measured 993 -> 594 inside ONE 25ms sample, no
      intermediate value at all. The clone is what makes it unjumpable, and the clone stops
      existing the moment the wrap lands -- so the answer is to WAIT for that moment. The wrap
      finishes its own forward travel, lands invisibly on the identical real slide, and only then
      does the queued request run, as an ordinary smooth move.

      It is stored as STEPS, not as an index: this.index is already the wrap's landing index while
      the wrap runs, so `index - this.index` is +1 for an arrow, -1 for the other, and the exact
      distance for a thumbnail. Three fast clicks therefore advance three photographs instead of
      collapsing into one. The cost is honest -- a click during a wrap waits out the ~400ms travel
      -- and it is the price of never showing a cut.
    */
    if (this.wrapping) {
      this.pendingSteps = (this.pendingSteps || 0) + (index - this.index);
      return;
    }


    /*
      THE RING IS DECIDED BEFORE THE RETARGET, NOT AFTER IT -- round 29, 2026-09-19. Naoya:
      "when i click fast, on the second image, it would not make a animation of going to next,
      but it shows to go back."

      These two tests used to sit BELOW the retarget branch, so a click arriving inside the 600ms
      settle window never reached wrap() at all: `index >= total` was folded to `wrapped = 0` and
      the track was told to scroll to photograph one's offset -- which is BACKWARD across the whole
      strip. Measured at 1440 on two clicks 150ms apart: forward to 445, then thirteen consecutive
      backward samples to 0. The slower the click, the more correct the gallery behaved, which is
      why every earlier round missed it.

      Above the retarget branch, the same press runs wrap('forward'), whose travel() retargets the
      in-flight smooth scroll onto the clone -- the browser redirects it without a discontinuity,
      so two fast clicks read as one accelerating forward move.
    */
    if (total > 1 && index >= total) return this.wrap('forward');
    if (total > 1 && index < 0) return this.wrap('back');

    if (this.settle) {
      this.setIndex(wrapped);
      this.track.scrollTo({ left: this.offsetOf(this.slides[wrapped]), behavior: behaviour || 'auto' });
      /*
        A RETARGET RE-ARMS THE SETTLE. It is a new move, so it needs a new 600ms window; without
        one, the FIRST move's timer is still the one running and it un-mutes the observer while
        this scroll is still travelling. Audit 2026-09-13, S03-07: every slide the retarget
        passed was then counted out loud, and `role="status"` announced "04, 03, 02, 01" for one
        press — measured at a 550ms gap, and at 400ms as "02, 01". The plain branch below has
        always called this; this branch returned without it.
      */
      this.afterScroll(() => {});
      return;
    }

    this.scripted = true;
    this.setIndex(wrapped);
    this.track.scrollTo({ left: this.offsetOf(this.slides[wrapped]), behavior: behaviour || 'auto' });
    this.afterScroll(() => {});
  }

  wrap(direction) {
    const last = this.slides.length - 1;
    const forward = direction === 'forward';
    const clone = forward ? this.tailClone : this.headClone;
    if (!clone) return this.goTo(forward ? 0 : last, 'auto');

    clone.hidden = false;
    this.scripted = true;
    this.wrapping = true;
    this.setIndex(forward ? 0 : last);

    const land = () => {
      this.wrapping = false;
      const real = this.slides[forward ? 0 : last];
      if (forward) {
        this.jumpTo(real);
        clone.hidden = true;
      } else {
        clone.hidden = true;
        this.jumpTo(real);
      }

      /*
        AND THE QUEUED REQUEST RUNS HERE, one frame later. The frame is not politeness: flipping
        the clone's `hidden` makes Chrome's scroll anchoring compensate for a removed slide AFTER
        this task ends (audit 2026-09-13, S08-01, which parked the track 41-54px off its snap
        point 16/16), so a scroll issued in this task is adjusted out from under itself. By the
        next frame the track is settled on a real slide and the queued move is an ordinary one.
      */
      const steps = this.pendingSteps || 0;
      this.pendingSteps = 0;
      if (steps && !(this.viewer && this.viewer.open)) {
        requestAnimationFrame(() => this.goTo(this.index + steps));
      }
    };

    const travel = () => {
      this.track.scrollTo({ left: this.offsetOf(clone), behavior: 'auto' });
      this.afterScroll(land);
    };

    if (forward) {
      travel();
    } else {
      /*
        The head clone grows the track to the LEFT, which would shunt the picture sideways by a
        whole slide, so this holds it still first.

        TWO THINGS MAKE THIS ONE LINE DELICATE, and the second one shipped broken for a round.
        `scrollLeft =` obeys `scroll-behavior` exactly as `scrollTo` does, so the compensation
        must be INSTANT or it becomes a second animation that cancels the one the arrow asked
        for — measured: every sample of the backward wrap read the same number, because the two
        scrolls annihilated and the landing ran inside 25ms. And it must settle in its own frame,
        or its `scrollend` reaches the listener belonging to the travel that follows it.

        It is also stated ABSOLUTELY, never as `scrollLeft + width`: Chrome's own scroll
        anchoring had already compensated for the clone by the time that line ran, so adding the
        width again started the journey TWO slides out and the reader saw a picture rewind past
        — the thing he asked to be rid of, in miniature. Measured 637 where 319 was meant.
      */
      this.setScrollLeft(this.offsetOf(this.slides[0]));
      requestAnimationFrame(travel);
    }

  }

  offsetOf(element) {
    return element.offsetLeft - this.track.offsetLeft;
  }

  /* The silent reposition. An INLINE `scroll-behavior` beats the stylesheet's `smooth`, which is
     the whole reason the first attempt failed: there is no scrollTo option that means "instant,
     whatever the CSS says" in every browser this ships to. */
  jumpTo(target) {
    this.setScrollLeft(this.offsetOf(target));
  }

  setScrollLeft(px) {
    const previous = this.track.style.scrollBehavior;
    this.track.style.scrollBehavior = 'auto';
    this.track.scrollLeft = px;
    this.track.style.scrollBehavior = previous;
  }

  /* Runs `done` when the track stops moving — `scrollend` where it exists, a timer everywhere
     else, and immediately if a second control is used mid-flight. While it is pending the
     observer is muted, so a multi-slide move (a thumbnail two along) cannot flicker either. */
  afterScroll(done) {
    clearTimeout(this.settleTimer);
    // Re-arming REPLACES the pending settle instead of stacking beside it: the previous `finish`
    // is still registered on `scrollend`, and when it fired it would clear the timer this call
    // is about to set.
    if (this.settle) this.track.removeEventListener('scrollend', this.settle);
    const finish = () => {
      clearTimeout(this.settleTimer);
      this.track.removeEventListener('scrollend', finish);
      this.settle = null;
      this.scripted = false;
      done();
    };
    this.settle = finish;
    this.track.addEventListener('scrollend', finish, { once: true });
    this.settleTimer = setTimeout(finish, 600);
  }

  buildClones() {
    const first = this.slides[0];
    const last = this.slides[this.slides.length - 1];
    this.tailClone = this.makeClone(first);
    this.headClone = this.makeClone(last);
    this.track.appendChild(this.tailClone);
    this.track.insertBefore(this.headClone, first);
  }

  makeClone(slide) {
    const clone = slide.cloneNode(true);
    clone.removeAttribute('id');
    clone.removeAttribute('data-shintop-slide');
    clone.removeAttribute('aria-current');
    clone.setAttribute('aria-hidden', 'true');
    clone.setAttribute('tabindex', '-1');
    clone.setAttribute('data-shintop-clone', '');
    clone.hidden = true;
    return clone;
  }

  /* ---------- controls ---------- */

  addArrows() {
    const stage = this.querySelector('[data-shintop-stage]');
    if (!stage) return;

    const make = (direction) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = `shintop-gallery__arrow shintop-gallery__arrow--${direction}`;
      button.setAttribute('aria-label', this.getAttribute(`data-label-${direction}`) || direction);
      button.innerHTML = this.querySelector(`[data-shintop-icon-${direction}]`).innerHTML;
      button.addEventListener('click', () => this.goTo(this.index + (direction === 'next' ? 1 : -1)));
      stage.appendChild(button);
      return button;
    };

    this.prevButton = make('prev');
    this.nextButton = make('next');
  }

  wireTiles() {
    this.tiles.forEach((tile, index) => {
      tile.addEventListener('click', (event) => {
        event.preventDefault();
        this.goTo(index);
      });
    });
  }

  /*
    KEYBOARD TRAVERSAL IS A CONTROL LIKE ANY OTHER, SO IT ROUTES THROUGH goTo().

    Audit 2026-09-13, S06-01 (P1). Tabbing through the stage left the browser to scroll each
    slide into view on its own, and at a FRACTIONAL slide width it does not finish: measured at
    1440 (slide 594.2px) the track stopped at scrollLeft 1189 for photographs 3 AND 4, so
    photograph 4 was never shown, the counter stayed on "3 of 5", and one real Enter then opened
    photograph 3 — the reader was shown and told the wrong picture. At 1024 (slide 407.0px,
    integer) the same walk is clean, which is why nothing caught it: it tracks sub-pixel
    rounding, not the code path. `this.index` is the single owner of what the viewer shows
    (§11.8), and it follows the IntersectionObserver alone — which never fires when the track
    never moves. Nothing here invents state; it gives focus the same door the arrows use.

    THE GUARD IS NOT DEFENSIVE, IT IS THE DIALOG'S OWN ORDER. `<dialog>` restores focus to the
    element that opened it, and it does that BEFORE firing `close` — measured: browse to
    photograph 3 in the viewer, close it, and `document.activeElement` is the slide-1 link while
    the index is 2. So an unguarded handler would read "focus is on slide 1, index is 2" and
    scroll the reader back to photograph 1 the instant they closed the viewer, silently undoing
    THE VIEWER AND THE STAGE SHARE AN INDEX (2026-09-13, round 24). The flag is raised for
    exactly the window between showModal() and the close event.
  */
  wireSlideFocus() {
    this.slides.forEach((slide, index) => {
      slide.addEventListener('focus', () => {
        if (this.viewerReturning) return;
        if (this.index !== index) this.goTo(index);
      });
    });
  }

  /*
    THE STAGE'S ARROW KEYS ARE A CONTROL LIKE ANY OTHER, SO THEY ROUTE THROUGH goTo().

    Round 28, 2026-09-19. Naoya: "with right arrow of the keyboard, why on modal it can navigate
    but not on the main product page ... after opening modal, control with right and left arrow
    ... and close the modal, somehow keyboard navigation still works on the product page's one.
    But not from the last image to the first image by clicking right arrow."

    All three sentences were one gap: until this method, the ONLY keydown listener in the
    component was the viewer's, so the stage had no keyboard contract at all. What looked like
    one was the browser. `<dialog>` returns focus to the slide <a> that opened it, and an arrow
    key scrolls the nearest scrollable ancestor of whatever is focused -- the track, which is
    `scroll-snap-type: x mandatory`, so that native scroll lands exactly one photograph along
    (measured on Alfort at 1440: scrollLeft 0 -> 594 on one ArrowRight) and the
    IntersectionObserver counts it out. Every such key reported `defaultPrevented: false`: nothing
    of ours ran. That is why the keys worked only AFTER a viewer visit, and why they could not
    cross the end -- a native scroll has no ring, so parked at scrollLeft max ArrowRight did
    nothing at all while the next ARROW BUTTON, which does route through goTo(), wrapped 1 -> 0 on
    the same page in the same second.

    preventDefault is load-bearing: without it the native scroll runs a second, competing move on
    top of ours, and two moves in flight at once is the "weird movement" round 24 was built to end.

    WHEN IT FIRES. Inside the gallery, always. Outside it, only when the reader has put focus into
    nothing -- a freshly loaded page is focused on <body>, which is the state he was in -- and only
    while the stage is on screen, so a gallery three bands above does not move where nobody can see
    it. It never fires on a modifier combination (Alt+Arrow is the browser's Back), in a text
    field, inside ANOTHER horizontal scroller (those cards want the native scroll they already
    have), or while the viewer is open: that dialog owns the keys, and its keydown bubbles to us
    because the dialog is our descendant.
  */
  wireKeys() {
    const TEXT = 'input, textarea, select, [contenteditable]';
    const INTERACTIVE = 'a[href], button, input, select, textarea, summary, [contenteditable]';

    const inOtherScroller = (node) => {
      for (let el = node; el && el !== document.body; el = el.parentElement) {
        if (el === this.track) return false;
        if (el.scrollWidth > el.clientWidth + 1) {
          const overflowX = getComputedStyle(el).overflowX;
          if (overflowX === 'auto' || overflowX === 'scroll') return true;
        }
      }
      return false;
    };

    /*
      ON SCREEN MEANS THE READER IS LOOKING AT IT, NOT THAT A SLIVER OF IT INTERSECTS. The test is
      the stage's CENTRE, because a key answered by a gallery one pixel below the header moves a
      photograph the reader cannot see and reads it out to a screen reader. Measured at the top of
      the page, where he is when he presses it: centre y 376 at 375x812, 377 at 768, 532 at 1440
      and 549 at 1800 -- inside every viewport. Scrolled 60% down the page it is quiet at all four.
    */
    const onScreen = () => {
      const stage = this.querySelector('[data-shintop-stage]') || this.track;
      const rect = stage.getBoundingClientRect();
      const middle = rect.top + rect.height / 2;
      return middle > 0 && middle < (window.innerHeight || document.documentElement.clientHeight);
    };

    /*
      A DRAWER OR MODAL OWNS THE SCREEN, AND THE GALLERY IS BEHIND IT. Dawn's cart drawer, menu
      drawer and modal dialog all announce themselves the same way -- `overflow-hidden` (or
      `overflow-hidden-<breakpoint>`) on <body> -- so one class test covers every one of them
      without this component naming any of them. Without it, an arrow pressed while the cart is
      open moves a photograph nobody can see and `role="status"` reads it out.
    */
    const modalOwnsTheScreen = () =>
      Array.prototype.some.call(document.body.classList, (name) => name.startsWith('overflow-hidden'));

    this.keyHandler = (event) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
      if (this.viewer && this.viewer.open) return;
      if (modalOwnsTheScreen()) return;

      const focused = document.activeElement;
      const inside = !!focused && this.contains(focused);

      if (inside) {
        if (focused.closest(TEXT)) return;
      } else {
        const parked = !focused || focused === document.body || focused === document.documentElement;
        if (!parked && (focused.matches(INTERACTIVE) || focused.closest(TEXT) || inOtherScroller(focused))) return;
        if (!onScreen()) return;
      }

      event.preventDefault();
      // Focus follows the photograph inside setIndex() -- this used to be the only place it
      // did, which is why a queued press left it behind (S08-001).
      this.goTo(this.index + (event.key === 'ArrowRight' ? 1 : -1));
    };

    document.addEventListener('keydown', this.keyHandler);
  }

  /*
    A DOCUMENT-LEVEL LISTENER OUTLIVES THE ELEMENT THAT ADDED IT. Dawn re-renders the media
    gallery's section on some variant changes, and a detached gallery still holding the keys would
    browse a track nobody can see.
  */
  disconnectedCallback() {
    if (this.keyHandler) document.removeEventListener('keydown', this.keyHandler);
    if (this.columnObserver) this.columnObserver.disconnect();
  }

  /* ---------- the pinned media column ----------
    A PINNED COLUMN TALLER THAN THE WINDOW HIDES ITS OWN FOOT (2026-09-27, round 35). With
    `shintop_sticky_media` on, the column pins at 32px; on a five-photograph product the two
    thumbnail rows make it 716px at 1199, taller than a 736px window less the pin, and the last
    tiles could not be scrolled to until the buy box ended. CSS cannot read an element's own
    height, so this publishes it and the stylesheet pins the column by its bottom instead
    whenever it does not fit (component-shintop-buy-box.css, sticky media column). No pinned
    column, no observer.
  */
  measureStickyColumn() {
    const column = this.closest('.shintop-media-sticky');
    if (!column || !('ResizeObserver' in window)) return;

    this.columnObserver = new ResizeObserver(() => {
      column.style.setProperty('--shintop-media-sticky-h', `${Math.ceil(column.offsetHeight)}px`);
    });
    this.columnObserver.observe(column);
  }

  /* ---------- the enlarged viewer ---------- */

  wireViewer() {
    if (!this.viewer || typeof this.viewer.showModal !== 'function') return;

    this.viewerImage = this.viewer.querySelector('[data-shintop-viewer-image]');
    this.viewerFrame = this.viewer.querySelector('[data-shintop-viewer-frame]');

    const open = (event) => {
      event.preventDefault();
      this.openViewer();
    };

    this.slides.forEach((slide) => slide.addEventListener('click', open));
    const expand = this.querySelector('[data-shintop-expand]');
    if (expand) expand.addEventListener('click', open);

    /*
      A CLONE IS CLICKED LIKE THE SLIDE IT COPIES (audit 2026-10-14, S03-007). For the ~300ms of a
      wrap the picture under the pointer is a clone -- an <a> to the 2,048px file that cloneNode()
      copied without the listener above -- so a click then LEFT THE PRODUCT PAGE for the raw
      image. Delegated, because the clones are built after this runs and are re-hidden every
      landing. It opens the viewer on `this.index`, which during a wrap is already the landing
      photograph -- the one the clone shows. Not `pointer-events: none`: that eats the click.
    */
    this.track.addEventListener('click', (event) => {
      if (event.target.closest('[data-shintop-clone]')) open(event);
    });

    this.viewer.querySelector('[data-shintop-close]').addEventListener('click', () => this.viewer.close());

    this.viewer.addEventListener('close', () => {
      if (this.settle) this.settle();
      this.jumpTo(this.slides[this.index]);
      /*
        The dialog has already handed focus back to the slide that OPENED it, and the reader may
        have browsed since (S03-006: opened on 1, closed on 3, focus on 1, Enter opened 3 again).
        Moved here explicitly rather than left to setIndex(): the observer re-reports the index
        only if jumpTo() actually moved the track, and only on its own timing. Still inside the
        guard's window, so the slide's focus handler stands down.
      */
      const slide = this.slides[this.index];
      if (document.activeElement !== slide && this.slides.includes(document.activeElement)) {
        slide.focus({ preventScroll: true });
      }
      this.viewerReturning = false;
    });

    const prev = this.viewer.querySelector('[data-shintop-viewer-prev]');
    const next = this.viewer.querySelector('[data-shintop-viewer-next]');
    if (prev) prev.addEventListener('click', () => this.select(this.index - 1));
    if (next) next.addEventListener('click', () => this.select(this.index + 1));

    const zoom = this.viewer.querySelector('[data-shintop-zoom]');
    if (zoom) zoom.addEventListener('click', () => this.toggleZoom());

    /*
      WHILE THE PHOTOGRAPH IS ZOOMED THE ARROWS PAN IT (S06-04, WCAG 2.1.1 Level A).

      Inspecting a 2x photograph was mouse-only — the hint has always said "scroll to
      inspect when zoomed", and scrolling is the one thing a keyboard could not do here,
      because this handler claimed both arrows for browsing. Worse than not panning: one
      ArrowRight browsed to the next photograph, and select() -> paintViewer() -> resetZoom()
      threw away the zoom the reader had just turned on. Measured on the unfixed tree at
      1440: data-zoom on -> off, aria-pressed true -> false, scrollLeft 476 -> 0, counter
      "1 of 5" -> "2 of 5", on a single real ArrowRight.

      The frame is the scroller (overflow: auto, and the zoomed img is 200% of it), so a pan
      is scrollBy on the frame. It declares no scroll-behavior, so this is an instant move,
      not an animation — nothing here depends on a settle timer. A quarter of the visible
      frame per press takes four presses to cross the picture in either axis.
    */
    const PAN = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] };

    this.viewer.addEventListener('keydown', (event) => {
      /*
        A MODIFIED ARROW IS THE BROWSER'S (audit 2026-10-14, S06-024(a)) -- Alt+Left is Back, and
        the modified arrows are the screen readers' and the OS's own commands. Both branches below
        called preventDefault on any arrow, so Alt+Left browsed a photograph instead of going
        back. The stage's handler (wireKeys) has always stood down here; this is its guard.
      */
      if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;

      const pan = PAN[event.key];
      if (pan && this.viewerFrame && this.viewerFrame.getAttribute('data-zoom') === 'on') {
        event.preventDefault();
        this.viewerFrame.scrollBy(
          (pan[0] * this.viewerFrame.clientWidth) / 4,
          (pan[1] * this.viewerFrame.clientHeight) / 4,
        );
        return;
      }

      if (this.slides.length < 2) return;
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.select(this.index - 1);
      }
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.select(this.index + 1);
      }
    });

    /*
      A CLICK OUTSIDE THE CARD IS THE BACKDROP -- MEASURED, NOT INFERRED FROM THE TARGET
      (audit 2026-10-14, S06-021). A click on the ::backdrop is delivered to the <dialog> itself,
      but so is a click on the dialog's own padding and on the gaps between its children, which
      have no element of their own. `event.target === this.viewer` therefore closed the viewer on
      a click 6px inside the card's edge, or in the 12px between the photograph and its controls
      -- one slip beside Next. Only a point outside the dialog's box is the backdrop.
    */
    this.viewer.addEventListener('click', (event) => {
      if (event.target !== this.viewer) return;
      const box = this.viewer.getBoundingClientRect();
      const inside =
        event.clientX >= box.left && event.clientX <= box.right && event.clientY >= box.top && event.clientY <= box.bottom;
      if (!inside) this.viewer.close();
    });
  }

  openViewer() {
    this.resetZoom();
    this.paintViewer();
    // Raised for the whole life of the dialog, and lowered by the `close` listener — which the
    // spec runs AFTER focus has been handed back to the slide that opened it. See wireSlideFocus.
    this.viewerReturning = true;
    this.viewer.showModal();
  }

  paintViewer() {
    const slide = this.slides[this.index];
    this.viewerImage.src = slide.getAttribute('href');
    this.viewerImage.alt = slide.getAttribute('data-label') || '';

    // With the stage's words off, the viewer keeps the product's own name as its heading —
    // the label belongs to the photograph, and that is the thing this setting turns off.
    const title = this.viewer.querySelector('[data-shintop-viewer-title]');
    if (title && this.getAttribute('data-words') === 'true' && slide.getAttribute('data-label')) {
      title.textContent = slide.getAttribute('data-label');
    }

    const counter = this.viewer.querySelector('[data-shintop-viewer-counter]');
    if (counter) {
      counter.textContent = (this.getAttribute('data-counter-template') || '%index% / %total%')
        .replace('%index%', String(this.index + 1))
        .replace('%total%', String(this.slides.length));
    }

    // S06-010: the viewer's own live region (the stage's is inert behind the modal).
    const status = this.viewer.querySelector('[data-shintop-viewer-status]');
    if (status) status.textContent = this.statusText(this.index);

    /*
      THE NEXT PHOTOGRAPH IS ALREADY HERE WHEN IT IS ASKED FOR (audit 2026-10-14, S08-002).
      The viewer shows the 2,048px file, which the stage (1,400px candidates) never loaded, so
      every step was a cold download while the counter and the alt already named the new
      picture: throttled to 200 kB/s, "2 of 7" sat over photograph 1 for 1.5-2.0 s.

      Both neighbours are fetched once this photograph has LOADED, not beside it: started
      together, three 2,048px files share one slow connection and the photograph the reader
      opened arrives three times later. Kept in a map so each file is asked for once and stays
      in the document's image cache, where the viewer's own `src` finds it.

      NO LOADING LOOK, by Naoya's call (2026-10-19, audit 2026-10-14 §17 #12). Pressing Next
      every 0.4 s at 200 kB/s still runs the counter ~0.65 s ahead of the photograph; he chose
      to leave that case bare — the photograph always arrives, and a spinner is one more thing
      on screen. Do not add one without asking him again.
    */
    if (this.viewerImage.complete) this.preloadNeighbours();
    else this.viewerImage.addEventListener('load', () => this.preloadNeighbours(), { once: true });

    this.resetZoom();
  }

  preloadNeighbours() {
    const total = this.slides.length;
    if (total < 2) return;
    if (!this.preloaded) this.preloaded = new Map();
    [this.index - 1, this.index + 1].forEach((i) => {
      const href = this.slides[((i % total) + total) % total].getAttribute('href');
      if (!href || this.preloaded.has(href)) return;
      const image = new Image();
      image.src = href;
      this.preloaded.set(href, image);
    });
  }

  toggleZoom() {
    const zoom = this.viewer.querySelector('[data-shintop-zoom]');
    const on = this.viewerFrame.getAttribute('data-zoom') === 'on';
    this.viewerFrame.setAttribute('data-zoom', on ? 'off' : 'on');
    // S06-011: the state moves, the name does not -- see the markup.
    zoom.setAttribute('aria-pressed', String(!on));

    if (on) {
      this.viewerFrame.scrollTo(0, 0);
    } else {
      this.viewerFrame.scrollTo(this.viewerFrame.clientWidth / 2, this.viewerFrame.clientHeight / 2);
    }
  }

  resetZoom() {
    if (!this.viewerFrame) return;
    const zoom = this.viewer.querySelector('[data-shintop-zoom]');
    this.viewerFrame.setAttribute('data-zoom', 'off');
    this.viewerFrame.scrollTo(0, 0);
    if (zoom) zoom.setAttribute('aria-pressed', 'false');
  }
}

if (!customElements.get('shintop-gallery')) {
  customElements.define('shintop-gallery', ShintopGallery);
}
